import type { InfiniteData, InfiniteQueryObserverResult } from '@tanstack/svelte-query';

/**
 * A relational count (e.g. Directus `<field>_func { count }`) counts junction
 * rows, including ones whose related item the caller cannot read — those come
 * back as null and get dropped when the page is transformed. Trusting the count
 * alone therefore leaves a "Show more" button that can never load anything.
 *
 * A page that comes back with fewer raw rows than the requested limit means the
 * server has run out of rows, whatever the count claims.
 */
export function hasMoreInfiniteItems<T>({
	query,
	totalCount,
	loadedCount,
	limit,
	getPageRows
}: {
	query: InfiniteQueryObserverResult<InfiniteData<T, unknown>, Error>;
	totalCount: number;
	loadedCount: number;
	limit: number;
	getPageRows: (page: T) => unknown[] | null | undefined;
}) {
	const pages = query.data?.pages;

	if (pages?.length) {
		const lastPageRows = getPageRows(pages[pages.length - 1])?.length ?? 0;

		if (lastPageRows < limit) {
			return false;
		}
	}

	return totalCount > loadedCount;
}
